<script setup lang="ts">
import DateBadge from '~/shared/ui/date-badge/DateBadge.vue';
import {
  type IEventPublisherDataDTO,
  mapPublisherEvents,
} from '~/shared/api/client/events/events-publisher.dto';
import { CLIENT_EVENTS_URL } from '~/shared/api/client/events/events.const';
import {
  CLIENT_EVENTS_MAIN_KEY,
  CLIENT_EVENTS_MAIN_URL,
} from '~/shared/api/client/publisher/publisher.const';
import { useCustomFetch } from '~/shared/lib/useCustomFetch';
import { getEventCardStub } from '~/shared/lib/getEventStubImage';
import { resizeImage, resizeImageSrcsetWidths } from '~/shared/lib/resizeImage';
import { CROP_RATIO_EVENTS_MAIN } from '~/shared/lib/crop-ratio.const';
import AdDisclosurePopover from '~/widgets/ad-disclosure/ui/AdDisclosurePopover.vue';
import { EVENTS_BANNER_COUNT } from '../../lib/const';

const { data: publisherData } = useCustomFetch<IEventPublisherDataDTO[]>(
  CLIENT_EVENTS_MAIN_URL,
  { key: CLIENT_EVENTS_MAIN_KEY },
);

const events = computed(() => {
  if (!publisherData.value) {
    return [];
  }
  const now = new Date();
  return mapPublisherEvents(publisherData.value)
    .filter((item) => new Date(item.event.end_time) >= now)
    .slice(0, EVENTS_BANNER_COUNT);
});

const activeIndex = ref(0);

watch(
  () => events.value.length,
  (len) => {
    if (len && activeIndex.value >= len) {
      activeIndex.value = len - 1;
    }
  },
);

const activeEvent = computed(() => events.value[activeIndex.value] ?? null);

/* Every box is close to the publisher crop, so cut to the same ratio. */
const IMAGE_ASPECT_RATIO = CROP_RATIO_EVENTS_MAIN[0]!.value;

const activeImage = computed(() => {
  if (!activeEvent.value) return null;

  const url =
    activeEvent.value.imageUrl || getEventCardStub(activeEvent.value.event.id);

  return {
    src: resizeImage(url, {
      width: 500,
      height: 500 / IMAGE_ASPECT_RATIO,
      mode: 'fill',
    }),
    srcset:
      resizeImageSrcsetWidths(url, [360, 500, 768, 1000, 1500], {
        mode: 'fill',
        aspectRatio: IMAGE_ASPECT_RATIO,
      }) || undefined,
  };
});
</script>

<template>
  <div v-if="events.length" :class="$style.banner">
    <div :class="$style.left">
      <Button
        v-for="(item, index) in events"
        :key="item.event.id"
        theme="white"
        :class="[$style.item, { [$style.item_active!]: index === activeIndex }]"
        :to="`${CLIENT_EVENTS_URL}${item.event.id}`"
        @mouseenter="activeIndex = index"
        @focus="activeIndex = index"
      >
        {{ item.event.name }}
      </Button>
    </div>

    <div v-if="activeEvent" :class="$style.right">
      <div :class="$style.photo">
        <AdDisclosurePopover
          :erid="activeEvent.event.erid"
          :company="activeEvent.event.company_information"
        />
        <NuxtLink
          :to="`${CLIENT_EVENTS_URL}${activeEvent.event.id}`"
          :class="$style.imageWrapper"
        >
          <img
            :key="activeEvent.event.id"
            :src="activeImage?.src"
            :srcset="activeImage?.srcset"
            sizes="(max-width: 767px) 100vw, (max-width: 1023px) 340px, (max-width: 1379px) 400px, 500px"
            :alt="activeEvent.event.name"
            :class="$style.image"
          />
        </NuxtLink>

        <div :class="$style.overlay">
          <DateBadge
            :start-time="activeEvent.event.start_time"
            :end-time="activeEvent.event.end_time"
            :month-only="activeEvent.event.month_only"
            variant="events-banner"
          />
        </div>

        <div :class="$style.action">
          <Button
            theme="primary"
            size="small"
            :class="$style.action__btn"
            :to="`${CLIENT_EVENTS_URL}${activeEvent.event.id}`"
          >
            Принять участие
          </Button>
        </div>
      </div>
    </div>
  </div>
</template>

<style module lang="scss">
$color-banner-bg: #fee8f0;

.banner {
  display: flex;
  border-bottom: 1px solid var(--color-gray-line);
}

.left {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  padding-top: var(--space-2-5x);
  gap: var(--space-1x);
}

.item {
  font-weight: 500;
  font-size: 18px;
  line-height: 110%;
  justify-content: flex-start;
  height: auto;
  min-height: auto;
  padding: 10px var(--space-2-5x);
  background: none;
  color: var(--color-black-1);
  transition: background-color 0.2s;

  &:hover {
    background-color: $color-banner-bg;
  }

  &_active {
    background-color: $color-banner-bg;
  }
}

.right {
  flex-shrink: 0;
  align-self: flex-start;
  width: 540px;
  padding: var(--space-2-5x);
  background-color: $color-banner-bg;
}

.photo {
  position: relative;
  width: 100%;
  height: 280px;
  background-color: var(--color-gray-bg);
  overflow: hidden;
}

.imageWrapper {
  display: block;
  width: 100%;
  height: 100%;
}

.image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.overlay {
  position: absolute;
  top: 0;
  left: 15px;
  display: flex;
  align-items: flex-start;
  gap: var(--space-2-5x);
  pointer-events: none;
}

.place {
  @include text_caption1_caps;
  color: var(--color-white);
  padding-top: var(--space-2-5x);
}

.action {
  position: absolute;
  bottom: var(--space-1-5x);
  right: var(--space-1-5x);
}

.action__btn {
  height: 40px;
  min-height: 40px;
  background-color: var(--color-black-2);
  border: 1px solid var(--color-gray-line);

  &:hover {
    background-color: var(--color-black-1);
    border-color: var(--color-white);
  }
}

@media (max-width: 1379px) {
  .right {
    width: 440px;
  }

  .photo {
    height: 230px;
  }
}

@media (max-width: 1023px) {
  .right {
    width: 380px;
  }

  .photo {
    height: 190px;
  }
}

@media (max-width: 767px) {
  .banner {
    flex-direction: column;
  }

  .right {
    width: 100%;
    order: -1;
  }

  .photo {
    height: auto;
    aspect-ratio: 32 / 17;
  }

  .left {
    flex-direction: column;
    height: auto;
    padding: 10px 0;
    overflow: visible;
  }

  .item {
    white-space: normal;
    flex-shrink: 1;
    padding: 10px var(--space-2-5x);
  }
}

@media (max-width: 479px) {
  .item {
    font-size: 14px;
  }
}
</style>
